/**
 * The team screen's two write actions, each behind a button rather than open
 * on the page.
 *
 * Both were forms sitting permanently above the list they act on, so the
 * resting screen carried four fields nobody had asked for yet. As dialogs the
 * screen reads as its status, its roster and one action.
 *
 * @module components/teams/TeamDialogs
 */

import React, { useState } from "react";

import {
  ModuleBuilderPicker,
  type PickerOption,
  StaffPicker,
} from "@/components/teams/TeamPickers";
import { Button, Modal } from "@/components/ui";
import { FormField, SelectField } from "@/components/ui/Form";
import type { ProgrammeResponsibility } from "@/server/types";

/** The reason a submit refuses, named on the control rather than implied. */
function submitProps(disabled: boolean, reason: string | undefined, describedBy: string) {
  return {
    // `aria-disabled`, not `disabled`: a disabled control is out of the
    // accessibility tree's reach for its own description, so the reason it
    // carries is never announced. `Button`'s click guard still refuses it.
    "aria-disabled": disabled || undefined,
    ...(disabled && reason ? { "aria-describedby": describedBy } : {}),
  };
}

export interface AddStaffDialogProps {
  open: boolean;
  onClose: () => void;
  programmeTitle: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly PickerOption[];
  /** The permissions this team may grant. */
  permissionOptions: readonly { value: ProgrammeResponsibility; label: string }[];
  /** The permission offered first. Viewer where it is allowed. */
  initialPermission: ProgrammeResponsibility | undefined;
  onAdd: (permission: ProgrammeResponsibility) => void;
  addDisabled: boolean;
  /** Why the action refuses, where it does. */
  disabledReason?: string;
}

export function AddStaffDialog({
  open,
  onClose,
  programmeTitle,
  value,
  onChange,
  options,
  permissionOptions,
  initialPermission,
  onAdd,
  addDisabled,
  disabledReason = undefined,
}: AddStaffDialogProps): React.ReactElement {
  const reasonId = "assignment-management-add-reason";
  // Chosen here rather than granted afterwards: adding always granted Viewer,
  // and a later Reviewer grant left the person holding both.
  const [permission, setPermission] = useState(initialPermission);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add staff"
      subtitle={programmeTitle}
      data-testid="assignment-management-add-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="user-plus"
            onClick={() => {
              if (!addDisabled && permission) {
                onAdd(permission);
              }
            }}
            {...submitProps(addDisabled, disabledReason, reasonId)}
            data-testid="assignment-management-add-responsibility"
          >
            Add staff
          </Button>
        </>
      }
    >
      <StaffPicker value={value} onChange={onChange} options={options} />
      <FormField
        label="Permission"
        htmlFor="assignment-management-add-permission"
        helpText="What they may do on this programme. More can be granted later."
      >
        <SelectField
          id="assignment-management-add-permission"
          value={permission ?? ""}
          onChange={(picked) => {
            setPermission(permissionOptions.find((option) => option.value === picked)?.value);
          }}
          options={[...permissionOptions]}
          data-testid="assignment-management-add-permission"
        />
      </FormField>
      {addDisabled && disabledReason ? (
        <p id={reasonId} className="mt-3 text-md text-muted">
          {disabledReason}
        </p>
      ) : null}
    </Modal>
  );
}

export interface AssignBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  programmeTitle: string;
  moduleId: string;
  onModuleChange: (value: string) => void;
  modules: readonly PickerOption[];
  moduleRefusal: string | undefined;
  ownerId: string;
  onOwnerChange: (value: string) => void;
  builders: readonly PickerOption[];
  ownerRefusal: string | undefined;
  onAssign: () => void;
  assignDisabled: boolean;
  disabledReason?: string;
}

export function AssignBuilderDialog({
  open,
  onClose,
  programmeTitle,
  onAssign,
  assignDisabled,
  disabledReason = undefined,
  ...picker
}: AssignBuilderDialogProps): React.ReactElement {
  const reasonId = "assignment-management-assign-reason";
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign a module builder"
      subtitle={programmeTitle}
      data-testid="assignment-management-assign-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="user-switch"
            onClick={() => {
              if (!assignDisabled) {
                onAssign();
              }
            }}
            {...submitProps(assignDisabled, disabledReason, reasonId)}
            data-testid="assignment-management-assign-module"
          >
            Assign module builder
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ModuleBuilderPicker {...picker} />
      </div>
      {assignDisabled && disabledReason ? (
        <p id={reasonId} className="mt-3 text-md text-muted">
          {disabledReason}
        </p>
      ) : null}
    </Modal>
  );
}
